import { FC, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button, Table, Checkbox, Typography } from 'antd';
import moment from 'moment';
import classnames from 'classnames';
import i18n from 'i18next';
import { Link } from 'react-router-dom';

import { downloadTransactions, getTransactions } from 'features/statement/actions';

import { DropDownComponent, RangeDatePicker } from 'componets/common';
import { RejectedMessage, TableWrapper } from 'componets/ui';

import { useAppSelector, useTranslate } from 'hooks';

import { IRangePicker, ColumnTypes } from 'models';

import { AppDispatch } from 'store';

import { RequestStatus } from 'utils/constants';
import Utils from 'utils/utils';

import { ReactComponent as DownloadIcon } from 'assets/icons/download.svg';
import { ReactComponent as DeleteIcon } from 'assets/icons/delete.svg';

import s from '../index.module.scss';
import '../index.scss';
import { selectUser } from '../../../../selectors';

const columns = [
	{
		title: i18n.t('dateText'),
		dataIndex: 'postDate',
		width: 110,
		render: (date: string) => {
			return <Typography.Text>{date} UTC</Typography.Text>;
		},
	},
	{
		title: i18n.t('detailsText'),
		dataIndex: 'type',
		render: (type: string) => {
			return reportTypes[type];
		},
		width: 270,
	},
	{
		title: i18n.t('projectIdText'),
		dataIndex: 'loanId',
		render: (loanId: number) => {
			return loanId ? (
				<Link to={`/account/projects/${loanId}`} className={s.greenText}>
					{loanId}
				</Link>
			) : (
				'-'
			);
		},
		width: 100,
	},
	{
		title: i18n.t('amountText'),
		dataIndex: 'amount',
		width: 100,
		render: (amount: number) => {
			const isNegative = amount < 0;

			return (
				<Typography.Text
					className={classnames({
						[s.redText]: isNegative,
					})}
				>
					{Utils.moneyFormat(+amount)}
				</Typography.Text>
			);
		},
	},
];

const PAGE_SIZE = 20;

export const Transactions: FC<{ type: string }> = ({ type }) => {
	const { transactionTypeText, loadMoreText, downloadTransactionsText } = useTranslate([
		'transactionTypeText',
		'loadMoreText',
		'downloadTransactionsText',
	]);

	const { transactions, status: requestStatus } = useAppSelector((state) => state.statement);
	const user = useAppSelector(selectUser);
	const transactionTypes = useAppSelector((state) => state.settings.globals.transactionTypes);

	const startDate = moment(user.registrationDate).isAfter(moment().startOf('year'))
		? moment(user.registrationDate)
		: moment().startOf('year');

	const [range, setRange] = useState<IRangePicker | undefined>({
		startDate,
		endDate: moment(),
	});

	const [transactionType, setTransactionType] = useState([]);
	const [offset, setOffset] = useState(0);
	const [isLoadMoreButton, setIsLoadMoreButton] = useState(true);
	const dispatch = useDispatch<AppDispatch>();

	useEffect(() => {
		if (transactionType.length > 0) {
			setTransactionType([]);
		}
	}, [type]);

	const params = useMemo(() => {
		return {
			startDate: range && moment(range.startDate).format('YYYY-MM-DD'),
			endDate: range && moment(range.endDate).format('YYYY-MM-DD'),
			transactionType,
			offset: offset,
			pageSize: PAGE_SIZE,
		};
	}, [offset, range, transactionType]);

	useEffect(() => {
		setOffset(0);
	}, [transactionType, range]);

	useEffect(() => {
		if (type === 'transactions') {
			dispatch(getTransactions({ params }))
				.unwrap()
				.then((d: any) => {
					const isMoreLoad = d?.length >= PAGE_SIZE;

					setIsLoadMoreButton(isMoreLoad);
				});
		}
	}, [dispatch, params]);

	const onDownload = () => {
		dispatch(
			downloadTransactions({
				params: {
					startDate: range && moment(range.startDate).format('YYYY-MM-DD'),
					endDate: range && moment(range.endDate).format('YYYY-MM-DD'),
					language: 'en',
					transactionType,
				},
			}),
		);
	};

	if (requestStatus === RequestStatus.REJECTED) {
		return <RejectedMessage />;
	}

	return (
		<div>
			<div className={s.reportsFilters}>
				<div>
					{transactions.length > 0 && (
						<div role="button" className="white-btn" onClick={onDownload}>
							<DownloadIcon />
							{downloadTransactionsText}
						</div>
					)}
				</div>

				<div className={s.reportsFilters}>
					<div className={s.rangeFilter}>
						<RangeDatePicker
							onChange={setRange}
							range={range}
							minDate={moment(user.registrationDate)}
							maxDate={moment()}
						/>
					</div>

					<div className="selectType">
						<DropDownComponent
							dropdownRender={() => (
								<ReportsTypesDropdown
									values={transactionTypes}
									setTransactionType={setTransactionType}
									transactionType={transactionType}
								/>
							)}
							label={transactionTypeText}
						/>
					</div>
				</div>
			</div>

			{transactionType.length > 0 && (
				<div className={s.currentTypes}>
					{transactionType.map((item) => {
						return (
							<Button
								key={item}
								className={s.currentTypeBtn}
								onClick={() => {
									setTransactionType(transactionType.filter((i) => item !== i));
								}}
							>
								{transactionTypesValues[item]} <DeleteIcon />
							</Button>
						);
					})}
				</div>
			)}

			<TableWrapper
				data={transactions}
				loading={requestStatus === RequestStatus.PENDING && transactions.length === 0}
			>
				<Table
					loading={requestStatus === RequestStatus.PENDING}
					pagination={false}
					scroll={{ x: 650 }}
					className="statementTable statementTable--reports"
					columns={columns as ColumnTypes}
					dataSource={transactions}
					footer={() => {
						return isLoadMoreButton ? (
							<div className={s.loadMore}>
								<Button type="primary" onClick={() => setOffset(offset + PAGE_SIZE)}>
									{loadMoreText}
								</Button>
							</div>
						) : (
							''
						);
					}}
				/>
			</TableWrapper>
		</div>
	);
};

const reportTypes: { [key: string]: string } = {
	BUYBACK_INTEREST: i18n.t('amountOfInterestText'),
	BUYBACK_PRINCIPAL: i18n.t('amountOfPrincipalText'),
	COMPENSATION_INTEREST: i18n.t('compensationInterestText'),
	DEPOSIT: i18n.t('depositTransactionText'),
	DEPOSIT_CORRECTION: i18n.t('depositCorrectionText'),
	INVESTMENT: i18n.t('investmentText'),
	INVESTMENT_PURCHASE_DIFF: i18n.t('investmentPurchaseText'),
	INVESTMENT_REFUND: i18n.t('investmentRefundText'),
	INVESTMENT_SALE_DIFF: i18n.t('investmentSaleDifferenceText'),
	INVESTMENT_SALE_FEE: i18n.t('investmentSaleFeeText'),
	REFERRAL_FEE: i18n.t('referralFeeText'),
	REPAYMENT_INTEREST: i18n.t('amountOfInterestText'),
	REPAYMENT_PRINCIPAL: i18n.t('amountOfPrincipalText'),
	TAX_WITHHOLDING: i18n.t('taxWithholdingText'),
	WITHDRAWAL: i18n.t('withdrawalText'),
	WITHDRAWAL_REFUND: i18n.t('withdrawalReturnText'),
};

const transactionTypesValues: { [key: string]: string } = {
	1: i18n.t('depositTransactionText'),
	2: i18n.t('withdrawalText'),
	3: i18n.t('principalRepaymentText'),
	4: i18n.t('interestPaymentText'),
	11: i18n.t('investmentText'),
	16: i18n.t('feesBonusesText'),
};

const ReportsTypesDropdown: FC<{
	values: { [key: string]: string };
	setTransactionType: any;
	transactionType: string[];
}> = ({ values, setTransactionType, transactionType }) => {
	return (
		<div className="dropdownContainer">
			{Object.keys(values).map((item) => {
				if (item === '0') return '';
				return (
					<div key={item} className="dropdownContainer__checkbox">
						<Checkbox
							checked={transactionType.includes(item)}
							onChange={(e) => {
								if (e.target.checked) {
									setTransactionType([...transactionType, item]);
								} else {
									setTransactionType(transactionType.filter((type) => type !== item));
								}
							}}
						>
							{transactionTypesValues[item]}
						</Checkbox>
					</div>
				);
			})}
		</div>
	);
};
